import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getApiBaseUrlWithFallback, getDomainBranding, shouldUseDefaultBranding } from '../config/environment';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Button,
  useMediaQuery,
  useTheme,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Menu,
  MenuItem,
  ListItemText,
  TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import EditIcon from '@mui/icons-material/Edit';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import QualifierSettings from '../components/QualifierSettings';
import InvitationMessageEditor from '../components/InvitationMessageEditor';
import WithdrawalManager from '../components/WithdrawalManager';
import { useAuth } from '../context/AuthContext';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { loadQualifierSettings } from '../services/qualifierSettingsService';
import { withdrawSkater, restoreSkater, updateWithdrawalNote, loadWithdrawnSkaters, formatWithdrawalDate } from '../services/withdrawalService';
import registrationService from '../services/registrationService';
import { getPublicInterclubFeatureToggles } from '../services/interclubFeatureToggleService';
import {
  getInvitationSummary,
  getInvitations,
  createInvitations,
  sendInvitations,
  updateInvitationMessage,
  updateInvitationStatus,
  DEFAULT_INVITATION_MESSAGE,
  DEFAULT_RUNNERS_UP_MESSAGE,
  INVITATION_STATUS,
  setGenderSplit,
  getInvitationActor,
  getInvitationActorSummary,
  getInvitationResponseNote,
} from '../services/qualifierInvitationService';
import { displayLevelTitle, parseSplitLevel, boysGroupLevel } from '../utils/genderSplit';

export default function Qualifiers() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated, isAdmin } = useAuth();
  const { user, getIdToken, isApprovedInterclubAdmin, isInterclubAdmin } = useFirebaseAuth();
  const canManageSplits = Boolean(isAdmin || isInterclubAdmin() || isApprovedInterclubAdmin());
  const [qualifiers, setQualifiers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDebugMode, setIsDebugMode] = useState(false);
  const [showPublicView, setShowPublicView] = useState(false);
  const [selectedSkater, setSelectedSkater] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);
  const [clubDetailOpen, setClubDetailOpen] = useState(false);
  const [showAllClubs, setShowAllClubs] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [withdrawalManagerOpen, setWithdrawalManagerOpen] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [withdrawnSkaters, setWithdrawnSkaters] = useState({});
  const [withdrawalsLoading, setWithdrawalsLoading] = useState({});
  const [clubNames, setClubNames] = useState([]);
  const [currentSeasonName, setCurrentSeasonName] = useState('');
  const [currentSeasonCompetitions, setCurrentSeasonCompetitions] = useState([]);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [brandingTitle, setBrandingTitle] = useState('');
  const [interclubToggles, setInterclubToggles] = useState({
    enableQualifiers: true,
  });
  const [toggleLoading, setToggleLoading] = useState(false);
  const [qualifierSettings, setQualifierSettings] = useState({
    columns: {
      rank: false,
      firstName: true,
      lastName: true,
      club: true,
      coach: true,
      competitions: true,
      qualifiedBadge: true,
      totalPoints: false,
      top3Scores: false,
      bestScore: false,
      levelAdvancement: false,
    },
    showRank: false,
    sortAlphabetically: true,
    showRunnersUp: true,
    runnersUpCount: 5,
  });
  const [emailExportOpen, setEmailExportOpen] = useState(false);
  const [emailExportLevel, setEmailExportLevel] = useState(null);
  const [emailExportData, setEmailExportData] = useState(null);
  const [emailExportLoading, setEmailExportLoading] = useState(false);
  const [emailCopySuccess, setEmailCopySuccess] = useState(false);
  const [runnersUpCopySuccess, setRunnersUpCopySuccess] = useState(false);
  const [invitationSummary, setInvitationSummary] = useState({});
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [invitationDialogOpen, setInvitationDialogOpen] = useState(false);
  const [invitationDialogLevel, setInvitationDialogLevel] = useState(null);
  const [invitationDialogAction, setInvitationDialogAction] = useState(null);
  const [invitationActionLoading, setInvitationActionLoading] = useState(false);
  const [invitationMessage, setInvitationMessage] = useState(DEFAULT_INVITATION_MESSAGE);
  const [runnersUpMessage, setRunnersUpMessage] = useState(DEFAULT_RUNNERS_UP_MESSAGE);
  const [invitationSaveSuccess, setInvitationSaveSuccess] = useState(false);
  const [inviteMenu, setInviteMenu] = useState({ anchorEl: null, invitation: null, skaterName: '' });
  const [inviteStatusSaving, setInviteStatusSaving] = useState(false);
  const [inviteNoteDialog, setInviteNoteDialog] = useState({
    open: false,
    status: '',
    invitation: null,
    skaterName: '',
    note: '',
    error: '',
  });
  const [splitDialog, setSplitDialog] = useState({ open: false, level: '', split: true, boys: [] });
  const [splitSaving, setSplitSaving] = useState(false);
  const [splitError, setSplitError] = useState('');

  const invitationPreviewSkaters = useMemo(() => {
    const qualifierFallback = { skaterName: 'Skater Name', rank: 1, totalPoints: 0 };
    const runnersUpFallback = { skaterName: 'Runner-up Name', rank: 13, totalPoints: 0 };
    if (!invitationDialogLevel) {
      return { qualifier: qualifierFallback, runnersUp: runnersUpFallback };
    }
    const byLevel = qualifiers.qualifiers || qualifiers || {};
    const list = [...(byLevel[invitationDialogLevel] || [])].sort(
      (a, b) => (b.totalPoints || 0) - (a.totalPoints || 0)
    );
    const toPreview = (skater, rank) => ({
      skaterName: skater.name || `${skater.firstName || ''} ${skater.lastName || ''}`.trim() || 'Skater Name',
      rank,
      totalPoints: skater.totalPoints || 0,
    });
    const qualifier = list[0] ? toPreview(list[0], 1) : qualifierFallback;
    const runnersUp = list[12] ? toPreview(list[12], 13) : runnersUpFallback;
    return { qualifier, runnersUp };
  }, [qualifiers, invitationDialogLevel]);

  const fetchQualifiers = useCallback(async () => {
    try {
      setLoading(true);

      // Get the appropriate API URL with fallback
      const baseUrl = await getApiBaseUrlWithFallback();
      if (isDebugMode) {
        console.log('Fetching qualifiers from:', baseUrl);
        console.log('Environment:', process.env.NODE_ENV);
      }

      // Add timeout for mobile devices
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000);

      const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...(isAdmin && !showPublicView && { 'x-admin-token': 'admin' }),
      };

      if (isDebugMode) {
        console.log('API Request Debug:', {
          url: `${baseUrl}/qualifiers`,
          isAuthenticated,
          isAdmin,
          showPublicView,
          headers,
          willSendAdminToken: isAdmin && !showPublicView
        });
      }

      // Build the URL with query parameters
      const url = new URL(`${baseUrl}/qualifiers`);
      if (isAdmin && !showPublicView) {
        // Pass the runners up count to the backend
        url.searchParams.set('runnersUpCount', qualifierSettings.runnersUpCount.toString());
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        mode: 'cors',
        signal: controller.signal,
        headers,
      });

      clearTimeout(timeoutId);
      if (isDebugMode) {
        console.log('Response received:', response.status, response.statusText);
        console.log('Response headers:', Object.fromEntries(response.headers.entries()));
        console.log('Response ok:', response.ok);
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Check if response is empty
      const responseText = await response.text();
      if (isDebugMode) {
        console.log('Response text length:', responseText.length);
        console.log('Response text preview:', responseText.substring(0, 200));
      }

      if (!responseText || responseText.trim() === '') {
        throw new Error('Empty response from server');
      }

      const data = JSON.parse(responseText);

      // Always log the data structure for debugging
      console.log('🔍 API Response Data:', {
        totalLevels: Object.keys(data).length,
        hasQualifiers: !!data.qualifiers,
        hasTopClubs: !!data.topClubs
      });

      if (isDebugMode) {
        console.log('Data received:', Object.keys(data).length, 'levels');
        console.log('Data structure:', data);
        console.log('Qualifiers data:', data.qualifiers);
        console.log('Top clubs data:', data.topClubs);
      }

      // Debug: Show what levels are in the qualifiers data
      if (data.qualifiers) {
        console.log('🔍 Levels found in qualifiers data:');
        Object.keys(data.qualifiers).forEach(level => {
          const skaters = data.qualifiers[level] || [];
          console.log(`  - ${level}: ${skaters.length} qualifiers`);

          // Show the first few skaters and their points for debugging
          if (skaters.length > 0) {
            console.log(`    First 5 skaters:`, skaters.slice(0, 5).map(s => ({
              name: s.name,
              points: s.totalPoints,
              rank: skaters.indexOf(s) + 1
            })));

            // Show if there are enough skaters for runners up
            if (skaters.length > 12) {
              console.log(`    Potential runners up (13+):`, skaters.slice(12).map(s => ({
                name: s.name,
                points: s.totalPoints,
                rank: skaters.indexOf(s) + 1
              })));
            } else {
              console.log(`    Not enough skaters for runners up (need >12, have ${skaters.length})`);
            }
          }
        });
      }

      // Validate data structure
      if (!data.qualifiers || typeof data.qualifiers !== 'object') {
        throw new Error('Invalid data structure: missing or invalid qualifiers data');
      }

      if (!data.topClubs || !Array.isArray(data.topClubs)) {
        throw new Error('Invalid data structure: missing or invalid topClubs data');
      }

      if (isDebugMode) {
        console.log('Data validation passed');
      }
      const topClubs = (data.topClubs || []).map((club) => {
        const fromBreakdown = Object.values(club.competitionBreakdown || {}).reduce(
          (sum, row) => sum + (Number(row.skaterCount) || 0),
          0
        );
        return fromBreakdown > 0 ? { ...club, totalPoints: fromBreakdown } : club;
      }).sort((a, b) => b.totalPoints - a.totalPoints);
      setQualifiers({ ...data, topClubs });
      setLoading(false);
    } catch (err) {
      console.error('Error fetching qualifiers:', err);
      if (isDebugMode) {
        console.error('Error details:', {
          name: err.name,
          message: err.message,
          stack: err.stack
        });
      }

      // Provide more specific error messages
      let errorMessage = 'Failed to fetch qualifier data. Please try again later.';
      if (err.name === 'AbortError') {
        errorMessage = 'Request timed out. Please check your internet connection and try again.';
      } else if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else if (err.message.includes('HTTP 500')) {
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (err.message.includes('HTTP 404')) {
        errorMessage = 'Qualifier data not found. Please contact support.';
      } else if (err.message.includes('CORS')) {
        errorMessage = 'Cross-origin request blocked. This may be a browser security issue.';
      } else if (err.message.includes('Invalid data structure')) {
        errorMessage = 'Data format error. Please contact support.';
      }

      setError(errorMessage);
      setLoading(false);
    }
  }, [isAuthenticated, showPublicView, isAdmin, isDebugMode, qualifierSettings.runnersUpCount]);

  const loadUserClubs = useCallback(async () => {
    if (!user) {
      setClubNames([]);
      return;
    }
    try {
      const token = await getIdToken();
      if (!token) return;
      const skatersResponse = await registrationService.getSkaters(token);
      const skaters = skatersResponse.skaters || skatersResponse || [];
      const names = new Set();
      skaters.forEach((skater) => {
        if (skater.club) {
          names.add(skater.club);
        }
      });
      setClubNames(Array.from(names));
    } catch (error) {
      console.error('Error loading user clubs:', error);
    }
  }, [user, getIdToken]);

  const loadQualifierEmails = useCallback(async (level) => {
    try {
      setEmailExportLoading(true);
      setEmailExportLevel(level);
      setEmailExportOpen(true);

      const baseUrl = await getApiBaseUrlWithFallback();
      const runnersUpCount = qualifierSettings.runnersUpCount || 5;
      const response = await fetch(
        `${baseUrl}/qualifiers/${encodeURIComponent(level)}/emails?top=12&runnersUpCount=${runnersUpCount}`,
        {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'x-admin-token': 'admin',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setEmailExportData(data);
    } catch (error) {
      console.error('Error loading qualifier emails:', error);
      setEmailExportData({ error: error.message });
    } finally {
      setEmailExportLoading(false);
    }
  }, [qualifierSettings.runnersUpCount]);

  const getExportGroups = useCallback((data) => {
    const allRows = [...(data?.qualifiers || []), ...(data?.runnersUp || [])];
    const seen = new Set();
    const uniqueRows = allRows.filter((row) => {
      if (seen.has(row.rank)) return false;
      seen.add(row.rank);
      return true;
    });
    const qualifierRows = uniqueRows.filter((row) => row.rank <= 12);
    const runnerRows = uniqueRows.filter((row) => row.rank > 12);
    const uniqueEmails = (rows) => [...new Set(rows.map((row) => row.parentEmail).filter(Boolean))];
    return {
      qualifierRows,
      runnerRows,
      qualifierEmails: uniqueEmails(qualifierRows),
      runnerEmails: uniqueEmails(runnerRows),
    };
  }, []);

  const handleCopyEmails = useCallback(async (emails, type = 'qualifiers') => {
    if (!emails?.length) return;

    try {
      await navigator.clipboard.writeText(emails.join(', '));
      if (type === 'runnersUp') {
        setRunnersUpCopySuccess(true);
        setTimeout(() => setRunnersUpCopySuccess(false), 2000);
      } else {
        setEmailCopySuccess(true);
        setTimeout(() => setEmailCopySuccess(false), 2000);
      }
    } catch (error) {
      console.error('Failed to copy emails:', error);
    }
  }, []);

  const handleDownloadCSV = useCallback(() => {
    if (!emailExportData) return;
    const { qualifierRows, runnerRows } = getExportGroups(emailExportData);
    const exportRows = [...qualifierRows, ...runnerRows];
    if (exportRows.length === 0) return;

    const headers = [
      'Type',
      'Rank',
      'Skater Name',
      'Club',
      'Total Points',
      'Parent/Guardian Name',
      'Parent/Guardian Email',
      'Parent/Guardian Phone',
      'Coach Name',
      'Coach Email',
      'Status',
    ];
    const toCsvRow = (type, q) => [
      type,
      q.rank,
      q.skaterName,
      q.club || '',
      q.totalPoints,
      q.parentName || '',
      q.parentEmail || '',
      q.parentPhone || '',
      q.coachName || '',
      q.coachEmail || '',
      q.parentEmail ? 'Has email' : q.skaterId || q.registered ? 'Registered, no email' : 'Not registered',
    ];
    const rows = [
      ...qualifierRows.map((q) => toCsvRow('Qualifier', q)),
      ...runnerRows.map((q) => toCsvRow('Runner-up', q)),
    ];

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${emailExportLevel}_qualifiers_emails.csv`;
    link.click();
  }, [emailExportData, emailExportLevel, getExportGroups]);

  const loadInvitationSummary = useCallback(async () => {
    if (!isAdmin) return;
    try {
      setInvitationLoading(true);
      const summary = await getInvitationSummary();
      setInvitationSummary(summary.byLevel || {});
    } catch (error) {
      console.error('Error loading invitation summary:', error);
    } finally {
      setInvitationLoading(false);
    }
  }, [isAdmin]);

  const handleConfirmGenderSplit = async () => {
    if (!splitDialog.level) return;
    try {
      setSplitSaving(true);
      setSplitError('');
      await setGenderSplit(splitDialog.level, splitDialog.split, { isAdmin: true });
      setSplitDialog({ open: false, level: '', split: true, boys: [] });
      await fetchQualifiers();
      if (isAdmin) await loadInvitationSummary();
    } catch (err) {
      setSplitError(err.message || 'Failed to update group split');
    } finally {
      setSplitSaving(false);
    }
  };

  const handleCreateInvitations = async (level) => {
    try {
      setInvitationActionLoading(true);
      await createInvitations(level, {
        includeRunnersUp: true,
        runnersUpCount: qualifierSettings.runnersUpCount,
        customMessage: invitationMessage.trim(),
        runnersUpMessage: runnersUpMessage.trim(),
      });
      await loadInvitationSummary();
      setInvitationDialogOpen(false);
    } catch (error) {
      console.error('Error creating invitations:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setInvitationActionLoading(false);
    }
  };

  const handleSyncMissingInvitations = async (level) => {
    try {
      setInvitationActionLoading(true);
      const result = await createInvitations(level, {
        includeRunnersUp: true,
        runnersUpCount: qualifierSettings.runnersUpCount,
        syncMissing: true,
      });
      await loadInvitationSummary();
      const names = (result.addedNames || []).join(', ');
      alert(
        `Added ${result.totalCreated} missing invitation${result.totalCreated === 1 ? '' : 's'}${names ? `: ${names}` : ''}`
      );
    } catch (error) {
      console.error('Error adding missing invitations:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setInvitationActionLoading(false);
    }
  };

  const handleSendInvitations = async (level) => {
    try {
      setInvitationActionLoading(true);
      await sendInvitations(level, {
        customMessage: invitationMessage.trim(),
        runnersUpMessage: runnersUpMessage.trim(),
      });
      await loadInvitationSummary();
      setInvitationDialogOpen(false);
    } catch (error) {
      console.error('Error sending invitations:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setInvitationActionLoading(false);
    }
  };

  const handleUpdateInvitationMessage = async (level) => {
    try {
      setInvitationActionLoading(true);
      setInvitationSaveSuccess(false);
      await updateInvitationMessage(level, {
        customMessage: invitationMessage.trim(),
        runnersUpMessage: runnersUpMessage.trim(),
      });
      setInvitationSaveSuccess(true);
      setTimeout(() => setInvitationSaveSuccess(false), 2500);
    } catch (error) {
      console.error('Error updating invitation message:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setInvitationActionLoading(false);
    }
  };

  const openInvitationDialog = async (level, action) => {
    setInvitationDialogLevel(level);
    setInvitationDialogAction(action);
    setInvitationSaveSuccess(false);
    setInvitationMessage(action === 'view' ? '' : DEFAULT_INVITATION_MESSAGE);
    setRunnersUpMessage(action === 'view' ? '' : DEFAULT_RUNNERS_UP_MESSAGE);
    setInvitationDialogOpen(true);

    if (action === 'send' || action === 'view' || action === 'edit') {
      try {
        const data = await getInvitations({ level, isAdmin: true });
        const invitations = data.invitations || [];
        const existingQualifierMessage = invitations.find(
          (inv) => inv.status !== INVITATION_STATUS.WAITLIST && inv.customMessage
        )?.customMessage || invitations.find((inv) => inv.customMessage)?.customMessage;
        const existingRunnersUpMessage = invitations.find((inv) => inv.runnersUpMessage)?.runnersUpMessage
          || invitations.find((inv) => inv.status === INVITATION_STATUS.WAITLIST && inv.customMessage)?.customMessage;
        if (existingQualifierMessage) {
          setInvitationMessage(existingQualifierMessage);
        } else if (action === 'edit') {
          setInvitationMessage(DEFAULT_INVITATION_MESSAGE);
        }
        if (existingRunnersUpMessage) {
          setRunnersUpMessage(existingRunnersUpMessage);
        } else if (action === 'edit' || action === 'send') {
          setRunnersUpMessage(DEFAULT_RUNNERS_UP_MESSAGE);
        }
      } catch (error) {
        console.error('Error loading invitation message:', error);
      }
    }
  };

  // Check for debug mode from URL query parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setIsDebugMode(urlParams.get('debug') === 'true');
  }, []);

  // Initial load effect
  useEffect(() => {
    fetchQualifiers();
  }, [fetchQualifiers]);

  useEffect(() => {
    loadUserClubs();
  }, [loadUserClubs]);

  useEffect(() => {
    if (isAdmin && !showPublicView) {
      loadInvitationSummary();
    }
  }, [isAdmin, showPublicView, loadInvitationSummary]);

  useEffect(() => {
    const loadCurrentSeasonCompetitions = async () => {
      try {
        const baseUrl = await getApiBaseUrlWithFallback();
        const seasonsResponse = await fetch(`${baseUrl}/seasons?current=true`);
        if (!seasonsResponse.ok) return;
        const seasons = await seasonsResponse.json();
        const currentSeasons = seasons.filter((season) => season.isCurrent);
        if (currentSeasons.length === 0) return;
        const season = currentSeasons[0];
        setCurrentSeasonName(season.name || '');
        setCurrentSeason(season);

        if (season.interclubId) {
          setToggleLoading(true);
          try {
            const toggles = await getPublicInterclubFeatureToggles(season.interclubId);
            setInterclubToggles({
              enableQualifiers: toggles.enableQualifiers !== false,
            });
            
            // Check domain-based branding first
            if (shouldUseDefaultBranding()) {
              const domainBranding = getDomainBranding();
              setBrandingTitle(domainBranding.brandingTitle || 'Interclub Competition Manager');
            } else {
              const interclubResponse = await fetch(`${baseUrl}/interclubs/${season.interclubId}`);
              if (interclubResponse.ok) {
                const interclub = await interclubResponse.json();
                setBrandingTitle(interclub?.brandingTitle || season.name || '');
              }
            }
          } catch (toggleError) {
            console.error('Error loading interclub toggles:', toggleError);
          } finally {
            setToggleLoading(false);
          }
        }

        const competitionsResponse = await fetch(`${baseUrl}/seasons/${season.id}/competitions`);
        if (!competitionsResponse.ok) return;
        const competitions = await competitionsResponse.json();
        const sorted = competitions.sort((a, b) => {
          const toDate = (value) => {
            if (!value) return null;
            if (value._seconds) return new Date(value._seconds * 1000);
            return new Date(value);
          };
          const aDate = toDate(a.startDate || a.date);
          const bDate = toDate(b.startDate || b.date);
          if (!aDate || !bDate) return 0;
          return aDate - bDate;
        });
        setCurrentSeasonCompetitions(sorted);
      } catch (error) {
        console.error('Error loading current season competitions:', error);
      }
    };
    loadCurrentSeasonCompetitions();
  }, []);

  const formatCompetitionDate = (competition) => {
    const toDate = (value) => {
      if (!value) return null;
      if (value.toDate && typeof value.toDate === 'function') return value.toDate();
      if (value._seconds) return new Date(value._seconds * 1000);
      return new Date(value);
    };
    const startDate = toDate(competition.startDate || competition.date);
    const endDate = toDate(competition.endDate || competition.startDate || competition.date);
    if (!startDate) return 'Date TBD';
    if (endDate && startDate.toDateString() !== endDate.toDateString()) {
      return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
    }
    return startDate.toLocaleDateString();
  };

  // Load saved qualifier settings from Firestore
  useEffect(() => {
    const loadSettings = async () => {
      try {
        console.log('🔍 Loading qualifier settings...');
        const savedSettings = await loadQualifierSettings();
        console.log('🔍 Settings loaded from Firestore:', savedSettings);

        if (savedSettings) {
          console.log('🔍 Setting qualifierSettings state to:', savedSettings);
          setQualifierSettings(savedSettings);
          if (isDebugMode) {
            console.log('Loaded saved qualifier settings:', savedSettings);
          }
        } else {
          console.log('🔍 No saved settings found, using defaults');
          if (isDebugMode) {
            console.log('No saved qualifier settings found, using default.');
          }
        }
      } catch (err) {
        console.error('Error loading saved qualifier settings:', err);
        if (isDebugMode) {
          console.error('Error details:', {
            name: err.name,
            message: err.message,
            stack: err.stack
          });
        }
      }
    };
    loadSettings();
  }, [isDebugMode]); // Re-run if debug mode changes

  // Debug effect to track qualifierSettings changes
  useEffect(() => {
    if (isDebugMode) {
      console.log('🔍 QualifierSettings state updated:', qualifierSettings);
      console.log('🔍 Current runnersUpCount:', qualifierSettings.runnersUpCount);
    }
  }, [qualifierSettings, isDebugMode]);

  // Cleanup effect for component unmounting
  useEffect(() => {
    return () => {
      // Cleanup any pending requests or timeouts if needed
      setQualifiers({});
      setLoading(false);
      setError(null);
    };
  }, []);

  const getLevelOrder = useCallback(() => {
    // Handle both possible data structures
    const qualifiersData = qualifiers.qualifiers || qualifiers;

    if (isDebugMode) {
      console.log('🔍 getLevelOrder called with:', qualifiersData);
    }

    // Get all levels that have qualifiers from the API response
    if (qualifiersData && typeof qualifiersData === 'object') {
      const levelsWithQualifiers = Object.keys(qualifiersData).filter(level =>
        qualifiersData[level] && Array.isArray(qualifiersData[level]) && qualifiersData[level].length > 0
      );

      if (isDebugMode) {
        console.log('🔍 Levels with qualifiers found:', levelsWithQualifiers);
      }

      // Sort levels in a logical order (Basic 1-6, Aspire 1-4, Pre-Preliminary, Adult levels)
      const levelOrder = [
        'Snowplow Sam',
        'Basic 1', 'Basic 2', 'Basic 3', 'Basic 4', 'Basic 5', 'Basic 6',
        'Aspire 1', 'Aspire 2', 'Aspire 3', 'Aspire 4',
        'Pre-Preliminary',
        'pre-preliminary', // Also include lowercase version for manually entered data
      ];

      // Add Adult levels in order
      for (let i = 1; i <= 6; i++) {
        levelOrder.push(`Adult ${i}`);
        levelOrder.push(`Adult${i}`); // Also include versions without spaces
      }

      if (isDebugMode) {
        console.log('🔍 Full level order array:', levelOrder);
      }

      const filteredLevels = [];
      levelOrder.forEach((level) => {
        if (levelsWithQualifiers.includes(level)) filteredLevels.push(level);
        const boysLevel = boysGroupLevel(level);
        if (levelsWithQualifiers.includes(boysLevel)) filteredLevels.push(boysLevel);
      });
      levelsWithQualifiers.forEach((level) => {
        if (!filteredLevels.includes(level)) filteredLevels.push(level);
      });

      if (isDebugMode) {
        console.log('🔍 Final filtered levels to display:', filteredLevels);
      }

      return filteredLevels;
    }

    if (isDebugMode) {
      console.log('🔍 No qualifiers data available');
    }

    return [];
  }, [qualifiers, isDebugMode]);

  const myClubRankings = useMemo(() => {
    if (!qualifiers.topClubs || clubNames.length === 0) return [];
    return qualifiers.topClubs.filter((club) => clubNames.includes(club.name));
  }, [qualifiers.topClubs, clubNames]);

  const loadWithdrawnSkatersForLevel = useCallback(async (level) => {
    if (!currentSeason) return;

    try {
      setWithdrawalsLoading(prev => ({ ...prev, [level]: true }));
      const withdrawn = await loadWithdrawnSkaters(level, currentSeason);

      if (isDebugMode) {
        console.log(`📥 Loaded withdrawals for ${level}:`, withdrawn);
      }

      setWithdrawnSkaters(prev => {
        const newState = {
          ...prev,
          [level]: withdrawn
        };
        if (isDebugMode) {
          console.log(`💾 Updated withdrawal state for ${level}:`, newState);
        }
        return newState;
      });
    } catch (error) {
      console.error('Failed to load withdrawn skaters for level:', level, error);
    } finally {
      setWithdrawalsLoading(prev => ({ ...prev, [level]: false }));
    }
  }, [isDebugMode, currentSeason]);

  // Load withdrawn skaters when qualifiers change
  useEffect(() => {
    if (currentSeason && qualifiers && Object.keys(qualifiers).length > 0) {
      // Get the actual skating levels, not the top-level API response keys
      const actualLevels = getLevelOrder();

      if (isDebugMode) {
        console.log('🔄 Loading withdrawals for actual skating levels:', actualLevels);
      }

      actualLevels.forEach(level => {
        loadWithdrawnSkatersForLevel(level);
      });
    }
  }, [qualifiers, loadWithdrawnSkatersForLevel, isDebugMode, getLevelOrder]);

  // Debug effect to track withdrawal state changes
  useEffect(() => {
    if (isDebugMode) {
      console.log('📊 Current withdrawal state:', withdrawnSkaters);
      console.log('📊 Current loading state:', withdrawalsLoading);
    }
  }, [withdrawnSkaters, withdrawalsLoading, isDebugMode]);

  const filterWithdrawnSkaters = (skaters, level) => {
    // Only filter if withdrawals are loaded for this level
    if (withdrawalsLoading[level]) {
      if (isDebugMode) {
        console.log(`⏳ Filtering ${level}: Still loading withdrawals, returning unfiltered`);
      }
      return skaters; // Return unfiltered while loading
    }

    const withdrawn = withdrawnSkaters[level] || [];
    // Try to match by multiple possible name fields
    const withdrawnNames = withdrawn.map(s =>
      s.skaterName || s.name || `${s.firstName} ${s.lastName}`.trim()
    );

    if (isDebugMode) {
      console.log(`🔍 Filtering ${level}:`, {
        totalSkaters: skaters.length,
        withdrawnCount: withdrawn.length,
        withdrawnNames,
        willFilter: showPublicView
      });
    }

    const filtered = skaters.filter(skater => {
      const skaterName = skater.name || `${skater.firstName} ${skater.lastName}`.trim();
      const isWithdrawn = withdrawnNames.includes(skaterName);
      if (isDebugMode && isWithdrawn) {
        console.log(`🚫 Filtering out withdrawn skater: ${skaterName}`);
      }
      return !isWithdrawn;
    });

    if (isDebugMode) {
      console.log(`✅ Filtered ${level}: ${skaters.length} → ${filtered.length} skaters`);
    }

    return filtered;
  };

  const competitionEventCount = (skater) =>
    Array.isArray(skater.competitionHistory) && skater.competitionHistory.length > 0
      ? skater.competitionHistory.length
      : skater.competitions;

  const getWithdrawnSkaterNames = (level) => {
    // Only return names if withdrawals are loaded for this level
    if (withdrawalsLoading[level]) {
      return [];
    }

    const withdrawn = withdrawnSkaters[level] || [];
    return withdrawn.map(s =>
      s.skaterName || s.name || `${s.firstName} ${s.lastName}`.trim()
    );
  };

  const isChampionshipOut = (invitation) =>
    invitation?.status === INVITATION_STATUS.DECLINED || invitation?.status === INVITATION_STATUS.WITHDRAWN;

  const getRosterStatus = (level, skaters, index) => {
    const invitation = getSkaterInvitation(level, skaters[index]);
    if (isChampionshipOut(invitation)) {
      return { status: 'Qualified', isRunnerUp: false };
    }
    let activeSlot = 0;
    for (let i = 0; i <= index; i += 1) {
      if (!isChampionshipOut(getSkaterInvitation(level, skaters[i]))) activeSlot += 1;
    }
    const isRunnerUp = activeSlot > 12;
    return { status: isRunnerUp ? 'Runner-up' : 'Qualified', isRunnerUp };
  };

  const getQualificationStatus = (rank) => {
    if (rank <= 12) return 'Qualified';
    return 'Runner-up';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Qualified': return 'success';
      case 'Runner-up': return 'warning';
      case 'Eligible': return 'info';
      default: return 'info'; // Default to info color for any other status
    }
  };

  const normalizeInviteName = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();

  const getLevelInvites = (level) => invitationSummary[level]?.invites || [];

  const getSkaterInvitation = (level, skater) => {
    const invites = getLevelInvites(level);
    if (!invites.length || !skater) return null;
    const skaterId = skater.skaterId || skater.id || null;
    if (skaterId) {
      const byId = invites.find((invite) => invite.skaterId && invite.skaterId === skaterId);
      if (byId) return byId;
    }
    const first = normalizeInviteName(skater.firstName);
    const last = normalizeInviteName(skater.lastName);
    const full = normalizeInviteName(skater.name || `${skater.firstName || ''} ${skater.lastName || ''}`);
    return (
      invites.find((invite) => {
        const inviteFirst = normalizeInviteName(invite.firstName);
        const inviteLast = normalizeInviteName(invite.lastName);
        if (first && last && inviteFirst === first && inviteLast === last) return true;
        return normalizeInviteName(invite.skaterName) === full;
      }) || null
    );
  };

  const getInvitationResponseDisplay = (invitation) => {
    if (!invitation) return { label: 'No invite', color: 'default', variant: 'outlined' };
    const actor = getInvitationActor(invitation);
    const actorSuffix = actor?.label ? ` · ${actor.label}` : '';
    switch (invitation.status) {
      case INVITATION_STATUS.ACCEPTED:
        return { label: `Accepted${actorSuffix}`, color: 'success', variant: 'filled' };
      case INVITATION_STATUS.PROMOTED:
        return { label: 'Promoted', color: 'info', variant: 'outlined' };
      case INVITATION_STATUS.SENT:
        return { label: 'Awaiting', color: 'info', variant: 'outlined' };
      case INVITATION_STATUS.PENDING:
        return { label: 'Not sent', color: 'default', variant: 'outlined' };
      case INVITATION_STATUS.DECLINED:
        return { label: `Declined${actorSuffix}`, color: 'error', variant: 'filled' };
      case INVITATION_STATUS.WITHDRAWN:
        return { label: `Withdrawn${actorSuffix}`, color: 'warning', variant: 'filled' };
      case INVITATION_STATUS.WAITLIST:
        return { label: 'Waitlist', color: 'default', variant: 'outlined' };
      default:
        return { label: invitation.status, color: 'default', variant: 'outlined' };
    }
  };

  const renderInvitationChip = (level, skater, size = 'small') => {
    if (!invitationSummary[level]) return null;
    const invitation = getSkaterInvitation(level, skater);
    const display = getInvitationResponseDisplay(invitation);
    const clickable = Boolean(isAdmin && !showPublicView && invitation?.id);
    const actorSummary = invitation ? getInvitationActorSummary(invitation) : '';
    const responseNote = getInvitationResponseNote(invitation);
    const tooltip = [actorSummary, responseNote].filter(Boolean).join(' — ') || display.label;
    return (
      <Tooltip title={tooltip}>
        <Chip
          label={display.label}
          color={display.color}
          size={size}
          variant={display.variant}
          onClick={
            clickable
              ? (event) => {
                  event.stopPropagation();
                  setInviteMenu({
                    anchorEl: event.currentTarget,
                    invitation,
                    skaterName: skater.name || invitation.skaterName || 'this skater',
                  });
                }
              : undefined
          }
          sx={clickable ? { cursor: 'pointer' } : undefined}
        />
      </Tooltip>
    );
  };

  const handleManagerInvitationStatus = async (status, noteOverride = null) => {
    const invitation = inviteNoteDialog.open ? inviteNoteDialog.invitation : inviteMenu.invitation;
    const skaterName = inviteNoteDialog.open ? inviteNoteDialog.skaterName : inviteMenu.skaterName;
    setInviteMenu({ anchorEl: null, invitation: null, skaterName: '' });
    if (
      (status === INVITATION_STATUS.DECLINED || status === INVITATION_STATUS.WITHDRAWN) &&
      noteOverride == null
    ) {
      setInviteNoteDialog({
        open: true,
        status,
        invitation,
        skaterName: skaterName || invitation?.skaterName || 'this skater',
        note: '',
        error: '',
      });
      return;
    }
    if (!invitation?.id) return;
    const note = String(noteOverride || '').trim();
    if (
      (status === INVITATION_STATUS.DECLINED || status === INVITATION_STATUS.WITHDRAWN) &&
      !note
    ) {
      setInviteNoteDialog((prev) => ({ ...prev, error: 'A note is required when declining as an admin.' }));
      return;
    }
    try {
      setInviteStatusSaving(true);
      await updateInvitationStatus(invitation.id, status, note || null, { isAdmin: true });
      setInviteNoteDialog({ open: false, status: '', invitation: null, skaterName: '', note: '', error: '' });
      await loadInvitationSummary();
    } catch (error) {
      console.error('Error updating invitation status:', error);
      if (inviteNoteDialog.open) {
        setInviteNoteDialog((prev) => ({ ...prev, error: error.message }));
      } else {
        alert(`Error: ${error.message}`);
      }
    } finally {
      setInviteStatusSaving(false);
    }
  };

  const renderConfigurableTable = (level, skaters) => {
    // Admin view: Always show all columns and rank, sort by points
    // Public view: Use qualifierSettings configuration
    const isAdminView = isAdmin && !showPublicView;

    // Filter out withdrawn skaters for public view
    const filteredSkaters = showPublicView ? filterWithdrawnSkaters(skaters, level) : skaters;

    console.log(`🔍 renderConfigurableTable for level ${level}:`, {
      isAdmin,
      showPublicView,
      isAdminView,
      totalSkaters: skaters.length,
      filteredSkaters: filteredSkaters.length,
      willShowRunnersUp: isAdminView && skaters.length > 12,
      runnersUpCount: Math.max(0, skaters.length - 12),
      qualifierSettings: qualifierSettings
    });

    if (isAdminView) {
      // Admin view: Full table with all columns, sorted by points, showing runners-up
      // Calculate how many runners-up to show based on withdrawn skaters
      const withdrawnCount = (withdrawnSkaters[level] || []).length;
      const baseQualifiers = 12;
      const maxRunnersUp = qualifierSettings.runnersUpCount || 5;
      const totalToShow = Math.min(baseQualifiers + maxRunnersUp, filteredSkaters.length);
      const hasRunnersUp = filteredSkaters.length > baseQualifiers;

      console.log(`🔍 Admin view calculation for ${level}:`, {
        totalSkaters: skaters.length,
        filteredSkaters: filteredSkaters.length,
        withdrawnCount,
        baseQualifiers,
        maxRunnersUp,
        totalToShow,
        hasRunnersUp,
        qualifierSettings: qualifierSettings,
        runnersUpCount: qualifierSettings.runnersUpCount
      });

      // Debug: Show the actual skaters that will be displayed
      console.log(`🔍 Skaters to display for ${level}:`, {
        totalToShow,
        actualSkatersToShow: filteredSkaters.slice(0, totalToShow).length,
        first12Skaters: filteredSkaters.slice(0, 12).map(s => ({ name: s.name, points: s.totalPoints })),
        runnersUpSkaters: filteredSkaters.slice(12, totalToShow).map(s => ({ name: s.name, points: s.totalPoints, rank: filteredSkaters.indexOf(s) + 1 }))
      });

      return (
        <TableContainer sx={{ width: '100%' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Rank</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Club</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Coach</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Total Points</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Top 3 Scores</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Competitions</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Best Score</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Level Advancement</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <Tooltip title="Click a status to mark accepted, declined, or awaiting">
                    <span>Invitation</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Withdrawal Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSkaters.slice(0, totalToShow).map((skater, index) => {
                const rows = filteredSkaters.slice(0, totalToShow);
                const displayRank = index + 1;
                const { status, isRunnerUp } = getRosterStatus(level, rows, index);

                return (
                  <TableRow
                    key={skater.skaterId || skater.name}
                    hover
                    onClick={() => handleSkaterClick(skater, index, level)}
                    sx={{
                      backgroundColor: isRunnerUp ? 'rgba(255, 193, 7, 0.1)' : 'inherit',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: isRunnerUp ? 'rgba(255, 193, 7, 0.2)' : 'rgba(0, 0, 0, 0.08)',
                      }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!isRunnerUp && index === 0 && '🥇'}
                        {!isRunnerUp && index === 1 && '🥈'}
                        {!isRunnerUp && index === 2 && '🥉'}
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {displayRank}
                        </Typography>
                        {isRunnerUp && (
                          <Chip label="Runner-up" size="small" color="warning" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>{skater.name}</TableCell>
                    <TableCell>{skater.club}</TableCell>
                    <TableCell>{skater.coach || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={skater.totalPoints}
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {skater.top3Scores ? (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {skater.top3Scores.map((score, idx) => (
                            <Chip
                              key={idx}
                              label={score}
                              color="primary"
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {skater.bestScore}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{competitionEventCount(skater)}</TableCell>
                    <TableCell>
                      <Chip
                        label={skater.bestScore}
                        color="secondary"
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {skater.levelAdvancement ? (
                        <Chip
                          label={`+${skater.levelAdvancement}`}
                          color="warning"
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No advancement
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={status}
                        color={getStatusColor(status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {renderInvitationChip(level, skater) || (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* Show withdrawal status for admin view */}
                      {(() => {
                        const withdrawnNames = getWithdrawnSkaterNames(level);
                        const skaterName = skater.name || `${skater.firstName} ${skater.lastName}`.trim();
                        const isWithdrawn = withdrawnNames.includes(skaterName);

                        if (isWithdrawn) {
                          return (
                            <Chip
                              label="Withdrawn"
                              color="error"
                              size="small"
                              variant="filled"
                            />
                          );
                        }
                        return (
                          <Typography variant="body2" color="text.secondary">
                            Active
                          </Typography>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      );
    } else {
      // Public view: Use qualifierSettings configuration
      const settings = qualifierSettings;
      // Sort skaters based on settings
      let sortedSkaters = [...skaters];
      if (settings.sortAlphabetically) {
        sortedSkaters.sort((a, b) => a.name.localeCompare(b.name));
      } else {
        sortedSkaters.sort((a, b) => {
          if (b.totalPoints !== a.totalPoints) {
            return b.totalPoints - a.totalPoints;
          }
          const aAdvancement = (a.levelAdvancement || 0);
          const bAdvancement = (b.levelAdvancement || 0);
          return bAdvancement - aAdvancement;
        });
      }

      // Limit to top 12 for public view
      sortedSkaters = sortedSkaters.slice(0, 12);

      return (
        <TableContainer sx={{ width: '100%' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {settings.columns.rank && (
                  <TableCell sx={{ fontWeight: 'bold' }}>Rank</TableCell>
                )}
                {settings.columns.firstName && (
                  <TableCell sx={{ fontWeight: 'bold' }}>First Name</TableCell>
                )}
                {settings.columns.lastName && (
                  <TableCell sx={{ fontWeight: 'bold' }}>Last Name</TableCell>
                )}
                {settings.columns.club && (
                  <TableCell sx={{ fontWeight: 'bold' }}>Club</TableCell>
                )}
                {settings.columns.coach && (
                  <TableCell sx={{ fontWeight: 'bold' }}>Coach</TableCell>
                )}
                {settings.columns.totalPoints && (
                  <TableCell sx={{ fontWeight: 'bold' }}>Total Points</TableCell>
                )}
                {settings.columns.top3Scores && (
                  <TableCell sx={{ fontWeight: 'bold' }}>Top 3 Scores</TableCell>
                )}
                {settings.columns.competitions && (
                  <TableCell sx={{ fontWeight: 'bold' }}>Competitions</TableCell>
                )}
                {settings.columns.bestScore && (
                  <TableCell sx={{ fontWeight: 'bold' }}>Best Score</TableCell>
                )}
                {settings.columns.qualifiedBadge && (
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedSkaters.map((skater, index) => {
                const status = getQualificationStatus(index + 1);

                return (
                  <TableRow
                    key={skater.name}
                    hover
                    onClick={() => handleSkaterClick(skater, index, level)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {settings.columns.rank && (
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {index === 0 && '🥇'}
                          {index === 1 && '🥈'}
                          {index === 2 && '🥉'}
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {index + 1}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                    {settings.columns.firstName && (
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {skater.firstName}
                      </TableCell>
                    )}
                    {settings.columns.lastName && (
                      <TableCell>
                        {skater.lastName}
                      </TableCell>
                    )}
                    {settings.columns.club && (
                      <TableCell>{skater.club}</TableCell>
                    )}
                    {settings.columns.coach && (
                      <TableCell>{skater.coach || 'N/A'}</TableCell>
                    )}
                    {settings.columns.totalPoints && (
                      <TableCell>
                        <Chip
                          label={skater.totalPoints}
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    )}
                    {settings.columns.top3Scores && (
                      <TableCell>
                        {skater.top3Scores ? (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {skater.top3Scores.map((score, idx) => (
                              <Chip
                                key={idx}
                                label={score}
                                color="primary"
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {skater.bestScore}
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    {settings.columns.competitions && (
                      <TableCell>{competitionEventCount(skater)}</TableCell>
                    )}
                    {settings.columns.bestScore && (
                      <TableCell>
                        <Chip
                          label={skater.bestScore}
                          color="secondary"
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    )}
                    {settings.columns.qualifiedBadge && (
                      <TableCell>
                        <Chip
                          label={status}
                          color={getStatusColor(status)}
                          size="small"
                        />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }
  };

  const handleSkaterClick = (skater, index, level) => {
    setSelectedSkater({ skater, index, level });
    setPopupOpen(true);
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
    setSelectedSkater(null);
  };

  // Mobile-friendly skater list item component
  const SkaterListItem = ({ skater, index, level, onItemClick, isRunnerUp, settings }) => {
    const displayRank = index + 1;
    const status = isRunnerUp ? 'Runner-up' : 'Qualified';
    const runnerUp = Boolean(isRunnerUp);

    // Use provided settings or default to showing everything for admin view
    const displaySettings = settings || {
      columns: {
        rank: true,
        firstName: true,
        lastName: true,
        club: true,
        coach: true,
        competitions: true,
        qualifiedBadge: true,
        totalPoints: true,
        top3Scores: true,
        bestScore: true,
        levelAdvancement: true,
      }
    };

    // Debug logging
    if (isDebugMode) {
      console.log(`🔍 SkaterListItem for ${skater.name}:`, {
        settings,
        displaySettings,
        top3ScoresVisible: displaySettings.columns.top3Scores,
        hasTop3Scores: skater.top3Scores && skater.top3Scores.length > 0
      });
    }

    return (
      <Paper
        elevation={1}
        sx={{
          mb: 1,
          borderRadius: 1,
          border: index < 3 && !runnerUp ? '2px solid' : '1px solid',
          borderColor: index < 3 && !runnerUp ? 'warning.main' : 'divider',
          backgroundColor: runnerUp ? 'rgba(255, 193, 7, 0.1)' : (index < 3 ? 'warning.50' : 'background.paper'),
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: runnerUp ? 'rgba(255, 193, 7, 0.2)' : 'action.hover',
            transform: 'translateY(-1px)',
            transition: 'all 0.2s ease-in-out'
          }
        }}
        onClick={() => onItemClick(skater, index, level)}
      >
        <Box sx={{ p: 2 }}>
          {/* Main row with rank, name, and key stats */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              {displaySettings.columns.rank && (
                <>
                  {!runnerUp && index === 0 && '🥇'}
                  {!runnerUp && index === 1 && '🥈'}
                  {!runnerUp && index === 2 && '🥉'}
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main', minWidth: '2rem' }}>
                    #{displayRank}
                  </Typography>
                </>
              )}
              <Typography variant="body1" sx={{ fontWeight: 'bold', flex: 1 }}>
                {skater.name}
              </Typography>
              {runnerUp && (
                <Chip
                  label="Runner-up"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ fontSize: '0.6rem', height: '20px' }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
              {displaySettings.columns.qualifiedBadge && (
                <Chip
                  label={status}
                  color={getStatusColor(status)}
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
              )}
              {isAdmin && !showPublicView && renderInvitationChip(level, skater)}
            </Box>
          </Box>

          {/* Secondary row with club and points */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              {displaySettings.columns.club && (
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  {skater.club}
                </Typography>
              )}
              {displaySettings.columns.coach && skater.coach && (
                <>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    •
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Coach: {skater.coach}
                  </Typography>
                </>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {displaySettings.columns.totalPoints && (
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {skater.totalPoints} pts
                </Typography>
              )}
              {displaySettings.columns.totalPoints && displaySettings.columns.bestScore && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  •
                </Typography>
              )}
              {displaySettings.columns.bestScore && (
                <Typography variant="body2" sx={{ color: 'secondary.main' }}>
                  {skater.bestScore}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Third row with competitions count and other details */}
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {displaySettings.columns.competitions && (
                <Typography variant="caption" color="text.secondary">
                  {competitionEventCount(skater)} competitions
                </Typography>
              )}
              {displaySettings.columns.levelAdvancement && isAdmin && !showPublicView && skater.levelAdvancement !== undefined && skater.levelAdvancement > 0 && (
                <>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    •
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                    +{skater.levelAdvancement} levels
                  </Typography>
                </>
              )}
            </Box>
            {displaySettings.columns.top3Scores && skater.top3Scores && skater.top3Scores.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Top 3:
                </Typography>
                {skater.top3Scores.slice(0, 3).map((score, idx) => (
                  <Chip
                    key={idx}
                    label={score}
                    color="primary"
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.6rem', height: '20px' }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    );
  };

  // Skater detail popup component
  const SkaterDetailPopup = ({ skater, index, level, open, onClose }) => {
    const fieldSize = 12 + (qualifierSettings.runnersUpCount || 5);
    const rows = ((qualifiers.qualifiers || qualifiers)[level] || []).slice(0, fieldSize);
    const displayRank = index + 1;
    const { status } = getRosterStatus(level, rows, index);
    const history = Array.isArray(skater.competitionHistory) ? skater.competitionHistory : [];
    const formatHistoryDate = (date) => {
      if (!date) return '';
      const parsed = new Date(date);
      if (Number.isNaN(parsed.getTime())) return '';
      return parsed.toLocaleDateString();
    };

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            height: isMobile ? '100%' : 'auto'
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {index === 0 && '🥇'}
              {index === 1 && '🥈'}
              {index === 2 && '🥉'}
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                #{index + 1}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={status}
                color={getStatusColor(status)}
                size="medium"
                sx={{ fontWeight: 'bold' }}
              />
              {isAdmin && !showPublicView && renderInvitationChip(level, skater, 'medium')}
            </Box>
          </Box>

          {/* Skater name and basic info */}
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
            {skater.name}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Club:</strong> {skater.club}
            </Typography>
            {skater.coach && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Coach:</strong> {skater.coach}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Key stats in grid */}
          {(qualifierSettings.columns.totalPoints || qualifierSettings.columns.bestScore) && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {qualifierSettings.columns.totalPoints && (
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.50', borderRadius: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {skater.totalPoints}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Points
                    </Typography>
                  </Box>
                </Grid>
              )}
              {qualifierSettings.columns.bestScore && (
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'secondary.50', borderRadius: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                      {skater.bestScore}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Best Score
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}

          {/* Top 3 scores */}
          {qualifierSettings.columns.top3Scores && skater.top3Scores && skater.top3Scores.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Top 3 Scores
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {skater.top3Scores.map((score, idx) => (
                  <Chip
                    key={idx}
                    label={score}
                    color="primary"
                    size="medium"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Additional info for all users */}
          {(qualifierSettings.columns.competitions || (qualifierSettings.columns.levelAdvancement && isAdmin && !showPublicView)) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                {qualifierSettings.columns.competitions && (
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.50', borderRadius: 2 }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                        {competitionEventCount(skater)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Competitions
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {qualifierSettings.columns.levelAdvancement && isAdmin && !showPublicView && skater.levelAdvancement !== undefined && (
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.50', borderRadius: 2 }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                        +{skater.levelAdvancement}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Level Advancement
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </>
          )}

          {history.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                Competition History
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Highlighted rows count toward the top 3 scores.
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Competition</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Level</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Place</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Points</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((comp, idx) => (
                      <TableRow
                        key={`${comp.competitionName}-${idx}`}
                        sx={{ bgcolor: comp.inTop3 ? 'primary.50' : 'inherit' }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {comp.competitionName}
                          </Typography>
                          {comp.date && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {formatHistoryDate(comp.date)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{comp.level || '-'}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={comp.place || '-'}
                            size="small"
                            color={comp.place === 1 ? 'warning' : comp.place <= 3 ? 'primary' : 'default'}
                            variant={comp.place && comp.place <= 3 ? 'filled' : 'outlined'}
                            sx={{ fontWeight: 600, minWidth: 32 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: comp.points > 0 ? 'success.main' : 'text.secondary' }}
                            >
                              {comp.points || 0}
                            </Typography>
                            {comp.inTop3 && (
                              <Chip label="Top 3" size="small" color="primary" variant="outlined" sx={{ height: 20 }} />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Close button */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{ minWidth: '120px' }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>
    );
  };

  if (!toggleLoading && currentSeason && interclubToggles.enableQualifiers === false) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Qualifiers are currently disabled for this interclub.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button
          variant="contained"
          onClick={fetchQualifiers}
          startIcon={<RefreshIcon />}
        >
          Retry
        </Button>
      </Box>
    );
  }

  const emailExportGroups = getExportGroups(emailExportData);
  const unregisteredCount = emailExportGroups.qualifierRows.filter(
    (row) => !row.parentEmail && !row.skaterId && !row.registered
  ).length;
  const registeredMissingEmailCount = emailExportGroups.qualifierRows.filter(
    (row) => (row.skaterId || row.registered) && !row.parentEmail
  ).length;

  const renderExportEmailCell = (qualifier) => {
    if (qualifier.parentEmail) {
      return (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
          {qualifier.parentEmail}
        </Typography>
      );
    }
    const unregistered = !qualifier.skaterId && !qualifier.registered;
    return (
      <Box>
        <Chip
          label={unregistered ? 'Not registered' : 'No email found'}
          color="warning"
          size="small"
          variant="outlined"
        />
        {unregistered && qualifier.club && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {qualifier.club}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{
      p: { xs: 2, sm: 3 },
      maxWidth: 1200,
      mx: 'auto',
      minHeight: '100vh'
    }}>
      <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
        <Typography
          variant={isMobile ? "h4" : "h3"}
          component="h1"
          gutterBottom
          sx={{ fontWeight: 'bold', color: 'primary.main' }}
        >
          {brandingTitle ? `${brandingTitle} Qualifiers` : 'Interclub Competition Manager Qualifiers'}
        </Typography>
        <Typography
          variant={isMobile ? "body1" : "h6"}
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          Current Season Qualification Status by Level
        </Typography>
        {isAdmin && !showPublicView && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Click a skater to see their competition history.
          </Typography>
        )}
        {isDebugMode && (
          <Box sx={{ mb: 2 }}>
            <Chip
              label="🔧 Debug Mode Active"
              color="warning"
              variant="filled"
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Only skaters with 3+ competitions are shown. Pre-Preliminary and Adult levels are included.
        </Typography>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: { xs: 1, sm: 2 },
          flexWrap: 'wrap',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center'
        }}>
          <Chip
            label="🏆 Top 12 qualify for Championship"
            color="success"
            variant="outlined"
            size={isMobile ? "small" : "medium"}
          />
          <Chip
            icon={<StarIcon />}
            label={isMobile ? "Top 3 scores count" : "Top 3 scores count (regardless of level)"}
            color="warning"
            variant="outlined"
            size={isMobile ? "small" : "medium"}
          />
          <Chip
            icon={<StarIcon />}
            label={isMobile ? "Min 3 competitions" : "Minimum 3 competitions required"}
            color="error"
            variant="outlined"
            size={isMobile ? "small" : "medium"}
          />
          {isDebugMode && (
            <Chip
              icon={<StarIcon />}
              label={`Environment: ${process.env.NODE_ENV}`}
              color="info"
              variant="outlined"
            />
          )}
          {isDebugMode && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                console.log('Testing connectivity...');
                console.log('Current URL:', window.location.href);
                console.log('User Agent:', navigator.userAgent);
                console.log('Platform:', navigator.platform);
                console.log('Language:', navigator.language);
                console.log('Environment:', process.env.NODE_ENV);
                console.log('API Base URL:', 'https://us-central1-pnic-scores.cloudfunctions.net/api');

                // Test the health endpoint
                const testUrl = 'https://us-central1-pnic-scores.cloudfunctions.net/api/health';

                fetch(testUrl)
                  .then(res => res.json())
                  .then(data => console.log('Health check success:', data))
                  .catch(err => console.log('Health check failed:', err.message));
              }}
            >
              Test Connection
            </Button>
          )}
        </Box>

        {/* Admin Public View Toggle */}
        {isAdmin && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: 1, borderColor: 'grey.300' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}>
              🔍 Admin View Toggle
            </Typography>
            <Button
              variant={showPublicView ? "contained" : "outlined"}
              color={showPublicView ? "info" : "primary"}
              size="small"
              onClick={() => setShowPublicView(!showPublicView)}
              sx={{ fontWeight: 'bold' }}
            >
              {showPublicView ? "👁️ Show Admin View" : "👤 Show Public View"}
            </Button>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
              {showPublicView
                ? "Currently showing what public users see (last initials only)"
                : "Currently showing full admin view (complete names)"
              }
            </Typography>

            {/* Settings Button */}
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'grey.300' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SettingsIcon />}
                onClick={() => setSettingsOpen(true)}
                sx={{ fontWeight: 'bold' }}
              >
                Configure Qualifier Page
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Club Rankings Section - Moved to top for prominence */}
      {qualifiers.topClubs && qualifiers.topClubs.length > 0 && (
        <Box sx={{ mt: { xs: 3, sm: 4 }, p: { xs: 2, sm: 3 }, bgcolor: 'gold.50', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'warning.main' }}>
          🏆 Club Rankings
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 3 }}>
          Top clubs ranked by 1 point per paid registered skater per eligible competition — including skaters who only have 1 or 2 events. Championship qualification is not required. Click any club for a breakdown.
        </Typography>

        {/* Show More/Less Controls */}
        {qualifiers.topClubs.length > 10 && (
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowAllClubs(!showAllClubs)}
              sx={{ fontWeight: 'bold' }}
            >
              {showAllClubs ? 'Show Less (Top 10)' : `Show More (${qualifiers.topClubs.length - 10} more clubs)`}
            </Button>
          </Box>
        )}

        {isMobile ? (
          // Mobile view: Compact list instead of table
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(showAllClubs ? qualifiers.topClubs : qualifiers.topClubs.slice(0, 10)).map((club, index) => (
              <Paper
                key={club.name}
                elevation={1}
                sx={{
                  borderRadius: 1,
                  border: index < 3 ? '2px solid' : '1px solid',
                  borderColor: index < 3 ? 'warning.main' : 'divider',
                  backgroundColor: index < 3 ? 'rgba(255, 215, 0, 0.1)' : 'background.paper',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: index < 3 ? 'rgba(255, 215, 0, 0.2)' : 'rgba(0, 0, 0, 0.04)',
                    transform: 'translateY(-1px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
                onClick={() => {
                  setSelectedClub(club);
                  setClubDetailOpen(true);
                }}
              >
                <Box sx={{ p: 2 }}>
                  {/* Main row with rank and club name */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      {index === 0 && '🥇'}
                      {index === 1 && '🥈'}
                      {index === 2 && '🥉'}
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main', minWidth: '2rem' }}>
                        #{index + 1}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', flex: 1 }}>
                        {club.name}
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {club.totalPoints}
                    </Typography>
                  </Box>

                  {/* Secondary row with skaters and competitions */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {club.uniqueSkaters} skaters
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {club.uniqueCompetitions} competitions
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          // Desktop view: Table
          <TableContainer component={Paper} elevation={2} sx={{ width: '100%' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Rank</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Club Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total Points</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Skaters</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Competitions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(showAllClubs ? qualifiers.topClubs : qualifiers.topClubs.slice(0, 10)).map((club, index) => (
                  <TableRow
                    key={club.name}
                    sx={{
                      backgroundColor: index < 3 ? 'rgba(255, 215, 0, 0.1)' : 'inherit',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: index < 3 ? 'rgba(255, 215, 0, 0.2)' : 'rgba(0, 0, 0, 0.08)',
                        transform: 'translateY(-1px)',
                        transition: 'all 0.2s ease-in-out'
                      }
                    }}
                    onClick={() => {
                      setSelectedClub(club);
                      setClubDetailOpen(true);
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {index === 0 && '🥇'}
                        {index === 1 && '🥈'}
                        {index === 2 && '🥉'}
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          #{index + 1}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{club.name}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {club.totalPoints}
                    </TableCell>
                    <TableCell>{club.uniqueSkaters}</TableCell>
                    <TableCell>{club.uniqueCompetitions}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Show More/Less Button for Desktop */}
            {qualifiers.topClubs.length > 10 && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={() => setShowAllClubs(!showAllClubs)}
                  sx={{ fontWeight: 'bold' }}
                >
                  {showAllClubs ? 'Show Less (Top 10)' : `Show More (${qualifiers.topClubs.length - 10} more clubs)`}
                </Button>
              </Box>
            )}
          </TableContainer>
        )}
      </Box>
      )}

      {/* My Club Rankings */}
      {myClubRankings.length > 0 && (
        <Box sx={{ mt: { xs: 3, sm: 4 }, p: { xs: 2, sm: 3 }, bgcolor: 'info.50', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'info.main' }}>
            🏅 My Club Rankings
          </Typography>
          <Typography variant="body2" paragraph sx={{ mb: 3 }}>
            Your club standings based on current PNIC rankings.
          </Typography>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {myClubRankings.map((club) => {
                const rank = qualifiers.topClubs.findIndex((entry) => entry.name === club.name) + 1;
                return (
                  <Paper key={club.name} elevation={1} sx={{ p: 2, borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        #{rank} {club.name}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                        {club.totalPoints}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {club.uniqueSkaters} skaters • {club.uniqueCompetitions} competitions
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={2} sx={{ width: '100%' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Rank</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Club</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Total Points</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Skaters</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Competitions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {myClubRankings.map((club) => {
                    const rank = qualifiers.topClubs.findIndex((entry) => entry.name === club.name) + 1;
                    return (
                      <TableRow key={club.name}>
                        <TableCell>#{rank}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{club.name}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>{club.totalPoints}</TableCell>
                        <TableCell>{club.uniqueSkaters}</TableCell>
                        <TableCell>{club.uniqueCompetitions}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Skater Rankings Section */}
      <Box sx={{ mt: { xs: 3, sm: 4 }, p: { xs: 2, sm: 3 }, bgcolor: 'primary.50', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          🎯 Individual Skater Rankings
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 3 }}>
          Top 12 qualifiers from each level who will compete in the championship round.
        </Typography>
      </Box>

      <Grid container spacing={isMobile ? 2 : 3}>
        {getLevelOrder().map((level) => (
          <Grid item xs={12} key={level}>
            <Paper elevation={2} sx={{ borderRadius: 2 }}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, width: '100%' }}>
                    <Typography variant={isMobile ? "h6" : "h5"} component="h2" sx={{ fontWeight: 'bold' }}>
                      {displayLevelTitle(level, qualifiers.genderSplits)}
                    </Typography>
                    <Chip
                      label={`${(qualifiers.qualifiers || qualifiers)[level].length} qualifiers`}
                      color="primary"
                      size={isMobile ? "small" : "medium"}
                    />
                    {withdrawalsLoading[level] && (
                      <Chip
                        label="Loading withdrawals..."
                        color="warning"
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {invitationLoading && (
                      <CircularProgress size={16} sx={{ ml: 1 }} />
                    )}

                    {/* Admin Action Buttons */}
                    {(canManageSplits || isAdmin) && !showPublicView && (
                      <Box sx={{ display: 'flex', gap: 1, ml: 'auto', alignItems: 'center' }}>
                        {canManageSplits && (() => {
                          const { baseLevel, isBoysGroup } = parseSplitLevel(level);
                          const hint = (qualifiers.genderSplitHints || {})[baseLevel];
                          const isSplit = Boolean((qualifiers.genderSplits || {})[baseLevel]);
                          if (isBoysGroup && isSplit) {
                            return (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<MergeTypeIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSplitError('');
                                  setSplitDialog({
                                    open: true,
                                    level: baseLevel,
                                    split: false,
                                    boys: hint?.boys || [],
                                  });
                                }}
                              >
                                Merge with girls
                              </Button>
                            );
                          }
                          if (!isBoysGroup && hint?.canSplit && !isSplit) {
                            return (
                              <Button
                                variant="outlined"
                                size="small"
                                color="secondary"
                                startIcon={<CallSplitIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSplitError('');
                                  setSplitDialog({
                                    open: true,
                                    level: baseLevel,
                                    split: true,
                                    boys: hint.boys || [],
                                  });
                                }}
                              >
                                Split boys group
                              </Button>
                            );
                          }
                          if (!isBoysGroup && isSplit) {
                            return (
                              <Chip size="small" color="secondary" variant="outlined" label="Girls group" />
                            );
                          }
                          return null;
                        })()}
                        {/* Invitation Status Indicator */}
                        {isAdmin && invitationSummary[level] && (() => {
                          const levelSummary = invitationSummary[level];
                          const qualifierSpotCount = Math.max(0, (levelSummary.total || 0) - (levelSummary.waitlist || 0));
                          const acceptedInvites = (levelSummary.invites || []).filter(
                            (invite) => invite.status === INVITATION_STATUS.ACCEPTED
                          );
                          const acceptedNames = acceptedInvites.map((invite) => invite.skaterName).filter(Boolean);
                          return (
                            <Tooltip
                              title={
                                acceptedNames.length
                                  ? `Accepted: ${acceptedNames.join(', ')}`
                                  : 'No one has accepted yet'
                              }
                            >
                              <Chip
                                size="small"
                                icon={
                                  acceptedInvites.length > 0 ? (
                                    <CheckCircleIcon />
                                  ) : levelSummary.sent > 0 ? (
                                    <HourglassEmptyIcon />
                                  ) : (
                                    <AddCircleIcon />
                                  )
                                }
                                label={`${acceptedInvites.length}/${qualifierSpotCount || 12} confirmed`}
                                color={
                                  acceptedInvites.length > 0
                                    ? 'success'
                                    : levelSummary.sent > 0
                                      ? 'info'
                                      : 'default'
                                }
                                variant="outlined"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openInvitationDialog(level, 'view');
                                }}
                                sx={{ cursor: 'pointer' }}
                              />
                            </Tooltip>
                          );
                        })()}
                        {isAdmin && !invitationSummary[level] && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddCircleIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              openInvitationDialog(level, 'create');
                            }}
                            sx={{ fontWeight: 'bold' }}
                            color="success"
                          >
                            Create Invitations
                          </Button>
                        )}
                        {isAdmin && invitationSummary[level]?.pending > 0 && (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<SendIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              openInvitationDialog(level, 'send');
                            }}
                            sx={{ fontWeight: 'bold' }}
                            color="primary"
                          >
                            Send Invites
                          </Button>
                        )}
                        {isAdmin && invitationSummary[level] && (() => {
                          const fieldSize = 12 + (qualifierSettings.runnersUpCount || 5);
                          const levelSkaters = ((qualifiers.qualifiers || qualifiers)[level] || []).slice(0, fieldSize);
                          const missingSkaters = levelSkaters.filter((skater) => !getSkaterInvitation(level, skater));
                          if (!missingSkaters.length) return null;
                          const missingNames = missingSkaters
                            .map((skater) => skater.name || `${skater.firstName || ''} ${skater.lastName || ''}`.trim())
                            .filter(Boolean)
                            .join(', ');
                          return (
                            <Tooltip title={missingNames ? `No invite: ${missingNames}` : 'Skaters in the championship field with no invitation'}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AddCircleIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSyncMissingInvitations(level);
                                }}
                                disabled={invitationActionLoading}
                                sx={{ fontWeight: 'bold' }}
                                color="success"
                              >
                                Add {missingSkaters.length} missing
                              </Button>
                            </Tooltip>
                          );
                        })()}
                        {isAdmin && invitationSummary[level] && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              openInvitationDialog(level, 'edit');
                            }}
                            sx={{ fontWeight: 'bold' }}
                          >
                            Edit Invite
                          </Button>
                        )}
                        {isAdmin && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EmailIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              loadQualifierEmails(level);
                            }}
                            sx={{ fontWeight: 'bold' }}
                            color="primary"
                          >
                            Export Emails
                          </Button>
                        )}
                        {isAdmin && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PersonOffIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLevel(level);
                              setWithdrawalManagerOpen(true);
                            }}
                            sx={{ fontWeight: 'bold' }}
                          >
                            Manage Withdrawals
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                  {isMobile ? (
                    // Mobile view: Cards instead of table
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {(() => {
                        // Admin view: Always show all skaters with full details, sorted by points
                        // Public view: Use qualifierSettings configuration
                        const isAdminView = isAdmin && !showPublicView;

                        if (isAdminView) {
                          // Admin view: Show all skaters (top 12 + runners up), sorted by points
                          let sortedSkaters = [...(qualifiers.qualifiers || qualifiers)[level]];

                          // Filter out withdrawn skaters but keep them for display with withdrawal status
                          const activeSkaters = filterWithdrawnSkaters(sortedSkaters, level);

                          // Enhanced sorting with multiple fallback criteria
                          activeSkaters.sort((a, b) => {
                            // Primary sort: total points (highest first)
                            if (b.totalPoints !== a.totalPoints) {
                              return b.totalPoints - a.totalPoints;
                            }

                            // Secondary sort: level advancement points (highest first)
                            const aAdvancement = (a.levelAdvancement || 0);
                            const bAdvancement = (b.levelAdvancement || 0);
                            if (bAdvancement !== aAdvancement) {
                              return bAdvancement - aAdvancement;
                            }

                            // Tertiary sort: higher scores in top 3 events
                            // Compare top 3 scores one by one (6,6,1 beats 6,4,3)
                            const aTop3 = (a.top3Scores || []).slice(0, 3);
                            const bTop3 = (b.top3Scores || []).slice(0, 3);

                            for (let i = 0; i < 3; i++) {
                              const aScore = aTop3[i] || 0;
                              const bScore = bTop3[i] || 0;
                              if (bScore !== aScore) {
                                return bScore - aScore; // Higher score wins
                              }
                            }

                            // Quaternary sort: number of competitions (more competitions = higher rank)
                            if (b.competitions !== a.competitions) {
                              return b.competitions - a.competitions;
                            }

                            // Quinary sort: earliest high score date (who got their high score first wins)
                            if (a.highScoreDate && b.highScoreDate) {
                              const dateCompare = new Date(a.highScoreDate) - new Date(b.highScoreDate);
                              if (dateCompare !== 0) return dateCompare; // Earlier date wins
                            } else if (a.highScoreDate && !b.highScoreDate) {
                              return -1;
                            } else if (!a.highScoreDate && b.highScoreDate) {
                              return 1;
                            }

                            // Final fallback: alphabetical by last name for complete determinism
                            return a.lastName.localeCompare(b.lastName);
                          });

                          // Calculate how many runners-up to show
                          const baseQualifiers = 12;
                          const maxRunnersUp = qualifierSettings.runnersUpCount || 5;
                          const totalToShow = Math.min(baseQualifiers + maxRunnersUp, activeSkaters.length);

                          // Show calculated number of skaters (top 12 + runners-up)
                          sortedSkaters = activeSkaters.slice(0, totalToShow);

                          // Admin view shows all columns
                          const adminSettings = {
                            columns: {
                              rank: true,
                              firstName: true,
                              lastName: true,
                              club: true,
                              coach: true,
                              competitions: true,
                              qualifiedBadge: true,
                              totalPoints: true,
                              top3Scores: true,
                              bestScore: true,
                              levelAdvancement: true,
                            }
                          };

                          const rowsToShow = sortedSkaters;
                          return rowsToShow.map((skater, index) => {
                            const { isRunnerUp } = getRosterStatus(level, rowsToShow, index);
                            return (
                              <SkaterListItem
                                key={skater.skaterId || skater.name}
                                skater={skater}
                                index={index}
                                level={level}
                                onItemClick={handleSkaterClick}
                                isRunnerUp={isRunnerUp}
                                settings={adminSettings}
                              />
                            );
                          });
                        } else {
                          // Public view: Use qualifierSettings configuration
                          const settings = qualifierSettings;

                          // Debug logging for public view
                          if (isDebugMode) {
                            console.log(`🔍 Public view for level ${level}:`, {
                              qualifierSettings,
                              top3ScoresVisible: settings.columns.top3Scores,
                              settings
                            });
                          }

                          let sortedSkaters = [...(qualifiers.qualifiers || qualifiers)[level]];

                          // Filter out withdrawn skaters for public view
                          sortedSkaters = filterWithdrawnSkaters(sortedSkaters, level);

                          // Sort skaters based on settings
                          if (settings.sortAlphabetically) {
                            sortedSkaters.sort((a, b) => a.name.localeCompare(b.name));
                          } else {
                            // Enhanced sorting with multiple fallback criteria
                            sortedSkaters.sort((a, b) => {
                              // Primary sort: total points (highest first)
                              if (b.totalPoints !== a.totalPoints) {
                                return b.totalPoints - a.totalPoints;
                              }

                              // Secondary sort: level advancement points (highest first)
                              const aAdvancement = (a.levelAdvancement || 0);
                              const bAdvancement = (b.levelAdvancement || 0);
                              if (bAdvancement !== aAdvancement) {
                                return bAdvancement - aAdvancement;
                              }

                              // Tertiary sort: higher scores in top 3 events
                              // Compare top 3 scores one by one (6,6,1 beats 6,4,3)
                              const aTop3 = (a.top3Scores || []).slice(0, 3);
                              const bTop3 = (b.top3Scores || []).slice(0, 3);

                              for (let i = 0; i < 3; i++) {
                                const aScore = aTop3[i] || 0;
                                const bScore = bTop3[i] || 0;
                                if (bScore !== aScore) {
                                  return bScore - aScore; // Higher score wins
                                }
                              }

                              // Quaternary sort: number of competitions (more competitions = higher rank)
                              if (b.competitions !== a.competitions) {
                                return b.competitions - a.competitions;
                              }

                              // Quinary sort: earliest high score date (who got their high score first wins)
                              if (a.highScoreDate && b.highScoreDate) {
                                const dateCompare = new Date(a.highScoreDate) - new Date(b.highScoreDate);
                                if (dateCompare !== 0) return dateCompare; // Earlier date wins
                              } else if (a.highScoreDate && !b.highScoreDate) {
                                return -1;
                              } else if (!a.highScoreDate && b.highScoreDate) {
                                return 1;
                              }

                              // Final fallback: alphabetical by last name for complete determinism
                              return a.lastName.localeCompare(b.lastName);
                            });
                          }

                          // Limit to top 12 for public view
                          sortedSkaters = sortedSkaters.slice(0, 12);

                          return sortedSkaters.map((skater, index) => {
                            return (
                              <SkaterListItem
                                key={skater.name}
                                skater={skater}
                                index={index}
                                level={level}
                                onItemClick={handleSkaterClick}
                                isRunnerUp={false}
                                settings={qualifierSettings} // Pass settings to SkaterListItem
                              />
                            );
                          });
                        }
                      })()}
                    </Box>
                  ) : (
                    // Desktop view: Table
                    renderConfigurableTable(level, (qualifiers.qualifiers || qualifiers)[level])
                  )}
                </AccordionDetails>
              </Accordion>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: { xs: 3, sm: 4 }, p: { xs: 2, sm: 3 }, bgcolor: 'blue.50', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Qualification Summary
        </Typography>
        <Typography variant="body2" paragraph>
          Total qualifiers across all levels: <strong>{Object.values((qualifiers.qualifiers || qualifiers) || {}).reduce((sum, level) => sum + level.length, 0)}</strong>
        </Typography>
        <Typography variant="body2" paragraph>
          Levels with qualifiers: <strong>{Object.keys((qualifiers.qualifiers || qualifiers) || {}).length}</strong>
        </Typography>
        {qualifiers.topClubs && (
          <>
            <Typography variant="body2" paragraph>
              Top club: <strong>{qualifiers.topClubs[0]?.name}</strong> with <strong>{qualifiers.topClubs[0]?.totalPoints} points</strong>
            </Typography>
            <Typography variant="body2" paragraph>
              Total clubs participating: <strong>{qualifiers.topClubs.length}</strong> clubs
            </Typography>
          </>
        )}
      </Box>



      <Box sx={{ mt: { xs: 3, sm: 4 }, p: { xs: 2, sm: 3 }, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          Championship Qualification Rules
        </Typography>
        <Typography variant="body2" paragraph>
          The PNIC Series Championship qualification system works as follows:
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li" variant="body2">
            <strong>Top 12 from each level</strong> qualify to compete in the championship round
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Top 3 scores count:</strong> Only the 3 highest scores from each skater (regardless of level) count towards qualification
          </Typography>
          <Typography component="li" variant="body2" sx={{ color: 'primary.main' }}>
            <strong>One competition per club:</strong> Only one competition per host club can count toward your top 3 scores (new this season)
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Minimum 3 competitions:</strong> Skaters must have competed in at least 3 competitions to qualify (Pre-Preliminary and Adult levels count)
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Qualify for highest level:</strong> Skaters qualify for their highest competition level, not their starting level
          </Typography>
          {/* <Typography component="li" variant="body2">
            <strong>Tie-breaking:</strong> When skaters have the same total points, additional points are awarded for level advancement
          </Typography> */}
          <Typography component="li" variant="body2">
            <strong>Points System:</strong> 1st place = 6 points, 2nd = 5, 3rd = 4, 4th = 3, 5th = 2, 6th = 1
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Solo Events:</strong> Skaters competing alone receive 3 points instead of 6
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Club Rankings:</strong> 1 point per paid skater per eligible event (1 or 2 competitions still count). Top 3 clubs receive awards at the championship.
          </Typography>
        </Box>
      </Box>

  {/* Qualifying Competitions List */ }
  <Box sx={{ mt: { xs: 3, sm: 4 }, p: { xs: 2, sm: 3 }, bgcolor: 'green.50', borderRadius: 2 }}>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'success.main' }}>
      🏆 Qualifying Competitions
    </Typography>
    <Typography variant="body2" paragraph sx={{ mb: 3 }}>
      {currentSeasonName ? `${currentSeasonName} competitions that count toward qualification:` : 'Current season competitions that count toward qualification:'}
    </Typography>

    {currentSeasonCompetitions.length === 0 ? (
      <Alert severity="info">No current season competitions found yet.</Alert>
    ) : (
      <Box component="ul" sx={{ pl: 2 }}>
        {currentSeasonCompetitions.map((competition) => (
          <Typography component="li" variant="body2" sx={{ mb: 1 }} key={competition.id || competition.name}>
            <strong>{competition.name}</strong>
            {competition.club ? ` – ${competition.club}` : ''}
            {competition.location ? ` – ${competition.location}` : ''}
            {' – '}
            {formatCompetitionDate(competition)}
          </Typography>
        ))}
      </Box>
    )}

    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      Note: Only paid, non-paused registrations and qualifying free skate/program events count toward series qualification and club points. Payment must be on or before the competition&apos;s last day.
    </Typography>
  </Box>

  {/* Skater Detail Popup */ }
  {
    selectedSkater && (
      <SkaterDetailPopup
        skater={selectedSkater.skater}
        index={selectedSkater.index}
        level={selectedSkater.level}
        open={popupOpen}
        onClose={handleClosePopup}
      />
    )
  }

  {/* Club Detail Dialog */ }
  {
    selectedClub && (
      <Dialog
        open={clubDetailOpen}
        onClose={() => setClubDetailOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              🏆 {selectedClub.name}
            </Typography>
            <Button
              onClick={() => setClubDetailOpen(false)}
              variant="outlined"
              size="small"
            >
              Close
            </Button>
          </Box>

          <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {selectedClub.totalPoints}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Points
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {selectedClub.uniqueSkaters}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Skaters
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                    {selectedClub.uniqueCompetitions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Competitions
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                    {selectedClub.uniqueCompetitions
                      ? Math.round((selectedClub.totalPoints / selectedClub.uniqueCompetitions) * 10) / 10
                      : 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg skaters / event
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Privacy Notice */}
          {!isAdmin || showPublicView ? (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'info.50', borderRadius: 2, border: '1px solid', borderColor: 'info.200' }}>
              <Typography variant="body2" color="info.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>ℹ️</span>
                <span>
                  <strong>Public View:</strong> Skater counts only.
                  {isAdmin && ' Switch to Admin View for full details.'}
                </span>
              </Typography>
            </Box>
          ) : (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'success.50', borderRadius: 2, border: '1px solid', borderColor: 'success.200' }}>
              <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>✅</span>
                <span>
                  <strong>Admin View:</strong> Full skater names and details are visible.
                </span>
              </Typography>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Club points are 1 per paid registered skater per eligible competition. A skater with only 1 or 2 counting events still earns those points for their club.
            {selectedClub.competitionBreakdown
              ? ` These events add up to ${Object.values(selectedClub.competitionBreakdown).reduce((sum, row) => sum + (row.skaterCount || 0), 0)} points.`
              : ''}
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Competition Breakdown
          </Typography>

          {Object.entries(selectedClub.competitionBreakdown || {}).map(([competition, data]) => (
            <Paper key={competition} elevation={1} sx={{ mb: 2, p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {competition}
                </Typography>
                <Chip
                  label={`${data.skaterCount} point${data.skaterCount !== 1 ? 's' : ''}`}
                  color="primary"
                  size="small"
                />
              </Box>
              {isAdmin && !showPublicView ? (
                // Admin view: Show full skater names
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {data.skaters.map((skater, index) => (
                    <Chip
                      key={index}
                      label={skater}
                      variant="outlined"
                      size="small"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  ))}
                </Box>
              ) : null}
            </Paper>
          ))}
        </Box>
      </Dialog>
    )
  }

  <Dialog
    open={splitDialog.open}
    onClose={() => !splitSaving && setSplitDialog({ open: false, level: '', split: true, boys: [] })}
    maxWidth="sm"
    fullWidth
  >
    <DialogTitle>
      {splitDialog.split ? `Split ${splitDialog.level} boys into their own group` : `Merge ${splitDialog.level} boys back with girls`}
    </DialogTitle>
    <DialogContent>
      {splitError && (
        <Alert severity="error" sx={{ mb: 2 }}>{splitError}</Alert>
      )}
      {splitDialog.split ? (
        <Typography variant="body2" paragraph>
          When there are 2 or more boys at a level other than Basic 1–6, they compete in their own
          championship group. This creates <strong>{splitDialog.level} Boys</strong> and frees those
          spots on the girls list.
        </Typography>
      ) : (
        <Typography variant="body2" paragraph>
          This puts the boys back on the mixed {splitDialog.level} list.
        </Typography>
      )}
      {(splitDialog.boys || []).length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {splitDialog.boys.map((name) => (
            <Chip key={name} label={name} size="small" />
          ))}
        </Box>
      )}
    </DialogContent>
    <DialogActions>
      <Button
        onClick={() => setSplitDialog({ open: false, level: '', split: true, boys: [] })}
        disabled={splitSaving}
      >
        Cancel
      </Button>
      <Button onClick={handleConfirmGenderSplit} variant="contained" disabled={splitSaving}>
        {splitSaving ? 'Saving…' : splitDialog.split ? 'Split group' : 'Merge groups'}
      </Button>
    </DialogActions>
  </Dialog>

  {/* Qualifier Settings Dialog */ }
  <QualifierSettings
    open={settingsOpen}
    onClose={() => setSettingsOpen(false)}
    onSave={setQualifierSettings}
  />

  {/* Withdrawn Skaters Section (Admin Only) */ }
  {
    isAdmin && !showPublicView && Object.values(withdrawnSkaters).some((list) => list?.length > 0) && (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
          Withdrawn Skaters
        </Typography>
        {Object.keys(withdrawnSkaters).map(level => {
          const withdrawn = withdrawnSkaters[level] || [];
          if (withdrawn.length === 0) return null;

          return (
            <Paper key={level} elevation={2} sx={{ mb: 3, p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: 'warning.main' }}>
                {level} - {withdrawn.length} Withdrawn
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Club</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Coach</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Total Points</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Withdrawal Note</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Withdrawn At</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {withdrawn.map((skater) => (
                      <TableRow key={skater.skaterId} sx={{ bgcolor: 'warning.50' }}>
                        <TableCell>{skater.skaterName}</TableCell>
                        <TableCell>{skater.club}</TableCell>
                        <TableCell>{skater.coach || 'N/A'}</TableCell>
                        <TableCell>{skater.totalPoints}</TableCell>
                        <TableCell>{skater.withdrawalNote}</TableCell>
                        <TableCell>
                          {formatWithdrawalDate(skater.withdrawnAt)}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={async () => {
                              try {
                                await restoreSkater(skater.skaterId);
                                await loadWithdrawnSkatersForLevel(level);
                                fetchQualifiers();
                              } catch (error) {
                                console.error('Error restoring skater:', error);
                              }
                            }}
                            startIcon={<PersonAddIcon />}
                          >
                            Restore
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          );
        })}
      </Box>
    )
  }

  {/* Withdrawal Manager Dialog */ }
  <WithdrawalManager
    open={withdrawalManagerOpen}
    onClose={() => setWithdrawalManagerOpen(false)}
    level={selectedLevel}
    season={currentSeason}
    skaters={(qualifiers.qualifiers || qualifiers)[selectedLevel] || []}
    onWithdraw={async (skater, note) => {
      try {
        await withdrawSkater(selectedLevel, skater, note, currentSeason);
        // Reload withdrawn skaters for this level
        await loadWithdrawnSkatersForLevel(selectedLevel);
        // Refresh the qualifiers to update the display
        fetchQualifiers();
      } catch (error) {
        console.error('Error withdrawing skater:', error);
      }
    }}
    onRestore={async (skater) => {
      try {
        const skaterId = `${selectedLevel}-${skater.firstName}-${skater.lastName}`.replace(/\s+/g, '-');
        await restoreSkater(skaterId);
        // Reload withdrawn skaters for this level
        await loadWithdrawnSkatersForLevel(selectedLevel);
        // Refresh the qualifiers to update the display
        fetchQualifiers();
      } catch (error) {
        console.error('Error restoring skater:', error);
      }
    }}
    onUpdateNote={async (skater, note) => {
      try {
        const skaterId = `${selectedLevel}-${skater.firstName}-${skater.lastName}`.replace(/\s+/g, '-');
        await updateWithdrawalNote(skaterId, note);
        // Reload withdrawn skaters for this level
        await loadWithdrawnSkatersForLevel(selectedLevel);
      } catch (error) {
        console.error('Error updating withdrawal note:', error);
      }
    }}
  />

  {/* Email Export Dialog */}
  <Dialog
    open={emailExportOpen}
    onClose={() => {
      setEmailExportOpen(false);
      setEmailExportData(null);
      setEmailExportLevel(null);
    }}
    maxWidth="lg"
    fullWidth
    fullScreen={isMobile}
  >
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          <EmailIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
          Export Qualifier Emails - {emailExportLevel}
        </Typography>
        <Button
          onClick={() => {
            setEmailExportOpen(false);
            setEmailExportData(null);
            setEmailExportLevel(null);
          }}
          variant="outlined"
          size="small"
        >
          Close
        </Button>
      </Box>

      {emailExportLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : emailExportData?.error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {emailExportData.error}
        </Alert>
      ) : emailExportData ? (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            Found <strong>{emailExportGroups.qualifierEmails.length}</strong> qualifier emails
            {emailExportGroups.runnerRows.length > 0 && (
              <>
                {' '}and <strong>{emailExportGroups.runnerEmails.length}</strong> runner-up emails
              </>
            )}
            {' '}for {emailExportLevel}.
            {emailExportGroups.qualifierRows.length > emailExportGroups.qualifierEmails.length && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {unregisteredCount > 0 && (
                  <>{unregisteredCount} qualifier(s) are not registered in the system, so no parent email is on file. </>
                )}
                {registeredMissingEmailCount > 0 && (
                  <>{registeredMissingEmailCount} registered qualifier(s) do not have a parent email on file.</>
                )}
              </Typography>
            )}
          </Alert>

          <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
              Quick Copy - Qualifier Emails
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                variant="contained"
                startIcon={<ContentCopyIcon />}
                onClick={() => handleCopyEmails(emailExportGroups.qualifierEmails, 'qualifiers')}
                disabled={!emailExportGroups.qualifierEmails.length}
                color={emailCopySuccess ? 'success' : 'primary'}
              >
                {emailCopySuccess ? 'Copied!' : `Copy ${emailExportGroups.qualifierEmails.length} Emails`}
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadCSV}
                disabled={!emailExportGroups.qualifierRows.length && !emailExportGroups.runnerRows.length}
              >
                Download CSV
              </Button>
            </Box>
            {emailExportGroups.qualifierEmails.length > 0 && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  bgcolor: 'white',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  maxHeight: 100,
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  wordBreak: 'break-all',
                }}
              >
                {emailExportGroups.qualifierEmails.join(', ')}
              </Box>
            )}
          </Paper>

          <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
              Quick Copy - Runner-up Emails
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                variant="contained"
                startIcon={<ContentCopyIcon />}
                onClick={() => handleCopyEmails(emailExportGroups.runnerEmails, 'runnersUp')}
                disabled={!emailExportGroups.runnerEmails.length}
                color={runnersUpCopySuccess ? 'success' : 'primary'}
              >
                {runnersUpCopySuccess ? 'Copied!' : `Copy ${emailExportGroups.runnerEmails.length} Emails`}
              </Button>
            </Box>
            {emailExportGroups.runnerEmails.length > 0 ? (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  bgcolor: 'white',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  maxHeight: 100,
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  wordBreak: 'break-all',
                }}
              >
                {emailExportGroups.runnerEmails.join(', ')}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No runner-up parent emails found for this level.
              </Typography>
            )}
          </Paper>

          {/* Detailed Table */}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Qualifier Details
          </Typography>
          <TableContainer component={Paper} elevation={1} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Rank</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Skater Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Club</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Points</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Parent/Guardian</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Coach</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Coach Email</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emailExportGroups.qualifierRows.map((qualifier) => (
                  <TableRow
                    key={`qualifier-${qualifier.rank}`}
                    sx={{
                      bgcolor: !qualifier.parentEmail ? 'warning.50' : 'inherit',
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {qualifier.rank === 1 && '🥇'}
                        {qualifier.rank === 2 && '🥈'}
                        {qualifier.rank === 3 && '🥉'}
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {qualifier.rank}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{qualifier.skaterName}</TableCell>
                    <TableCell>{qualifier.club || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={qualifier.totalPoints}
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{qualifier.parentName || '-'}</TableCell>
                    <TableCell>{renderExportEmailCell(qualifier)}</TableCell>
                    <TableCell>
                      {qualifier.parentPhone ? (
                        <Typography variant="body2">{qualifier.parentPhone}</Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>{qualifier.coachName || '-'}</TableCell>
                    <TableCell>
                      {qualifier.coachEmail ? (
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                          }}
                        >
                          {qualifier.coachEmail}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {emailExportGroups.runnerRows.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                Runner-up Details
              </Typography>
              <TableContainer component={Paper} elevation={1}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Rank</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Skater Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Club</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Points</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Parent/Guardian</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Coach</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Coach Email</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {emailExportGroups.runnerRows.map((qualifier) => (
                      <TableRow
                        key={`runner-${qualifier.rank}`}
                        sx={{
                          bgcolor: !qualifier.parentEmail ? 'warning.50' : 'inherit',
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {qualifier.rank}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{qualifier.skaterName}</TableCell>
                        <TableCell>{qualifier.club || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={qualifier.totalPoints}
                            color="default"
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{qualifier.parentName || '-'}</TableCell>
                        <TableCell>{renderExportEmailCell(qualifier)}</TableCell>
                        <TableCell>
                          {qualifier.parentPhone ? (
                            <Typography variant="body2">{qualifier.parentPhone}</Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>{qualifier.coachName || '-'}</TableCell>
                        <TableCell>
                          {qualifier.coachEmail ? (
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                              }}
                            >
                              {qualifier.coachEmail}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      ) : null}
    </Box>
  </Dialog>

  {/* Invitation Management Dialog */}
  <Dialog
    open={invitationDialogOpen}
    onClose={() => setInvitationDialogOpen(false)}
    maxWidth="md"
    fullWidth
  >
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
        {invitationDialogAction === 'create' && `Create Invitations - ${invitationDialogLevel}`}
        {invitationDialogAction === 'send' && `Send Invitations - ${invitationDialogLevel}`}
        {invitationDialogAction === 'edit' && `Edit Invitation - ${invitationDialogLevel}`}
        {invitationDialogAction === 'view' && `Invitation Status - ${invitationDialogLevel}`}
      </Typography>

      {invitationDialogAction === 'create' && (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            This will create championship invitations for the top 12 qualifiers in {invitationDialogLevel},
            plus {qualifierSettings.runnersUpCount} runners-up on the waitlist.
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            After creating invitations, you can review them before sending.
            Parents/guardians will receive an invitation in their dashboard where they can accept or decline.
          </Typography>
          <InvitationMessageEditor
            qualifierMessage={invitationMessage}
            onQualifierMessageChange={setInvitationMessage}
            runnersUpMessage={runnersUpMessage}
            onRunnersUpMessageChange={setRunnersUpMessage}
            level={invitationDialogLevel}
            qualifierPreview={invitationPreviewSkaters.qualifier}
            runnersUpPreview={invitationPreviewSkaters.runnersUp}
          />
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setInvitationDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              startIcon={invitationActionLoading ? <CircularProgress size={16} /> : <AddCircleIcon />}
              onClick={() => handleCreateInvitations(invitationDialogLevel)}
              disabled={invitationActionLoading}
            >
              Create Invitations
            </Button>
          </Box>
        </>
      )}

      {invitationDialogAction === 'send' && (
        <>
          <Alert severity="warning" sx={{ mb: 3 }}>
            This will mark {invitationSummary[invitationDialogLevel]?.pending || 0} pending invitations as "Sent".
            Parents/guardians will see the invitation on their dashboard and can accept or decline.
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Make sure you have exported and emailed the qualifier list before marking as sent.
          </Typography>
          <InvitationMessageEditor
            qualifierMessage={invitationMessage}
            onQualifierMessageChange={setInvitationMessage}
            runnersUpMessage={runnersUpMessage}
            onRunnersUpMessageChange={setRunnersUpMessage}
            level={invitationDialogLevel}
            qualifierPreview={invitationPreviewSkaters.qualifier}
            runnersUpPreview={invitationPreviewSkaters.runnersUp}
          />
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setInvitationDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={invitationActionLoading ? <CircularProgress size={16} /> : <SendIcon />}
              onClick={() => handleSendInvitations(invitationDialogLevel)}
              disabled={invitationActionLoading}
            >
              Mark as Sent
            </Button>
          </Box>
        </>
      )}

      {invitationDialogAction === 'edit' && (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            Update the qualifier and runner-up messages for all {invitationDialogLevel} invitations. Parents/guardians will see the new text the next time they open their dashboard.
          </Alert>
          {invitationSaveSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Invitation messages saved.
            </Alert>
          )}
          <InvitationMessageEditor
            qualifierMessage={invitationMessage}
            onQualifierMessageChange={(value) => {
              setInvitationSaveSuccess(false);
              setInvitationMessage(value);
            }}
            runnersUpMessage={runnersUpMessage}
            onRunnersUpMessageChange={(value) => {
              setInvitationSaveSuccess(false);
              setRunnersUpMessage(value);
            }}
            level={invitationDialogLevel}
            qualifierPreview={invitationPreviewSkaters.qualifier}
            runnersUpPreview={invitationPreviewSkaters.runnersUp}
          />
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setInvitationDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={invitationActionLoading ? <CircularProgress size={16} /> : <EditIcon />}
              onClick={() => handleUpdateInvitationMessage(invitationDialogLevel)}
              disabled={invitationActionLoading}
            >
              Save Message
            </Button>
          </Box>
        </>
      )}

      {invitationDialogAction === 'view' && invitationSummary[invitationDialogLevel] && (
        <>
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {invitationSummary[invitationDialogLevel].accepted || 0}
                  </Typography>
                  <Typography variant="body2">Accepted</Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.50' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                    {invitationSummary[invitationDialogLevel].sent || 0}
                  </Typography>
                  <Typography variant="body2">Awaiting Response</Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                    {invitationSummary[invitationDialogLevel].waitlist || 0}
                  </Typography>
                  <Typography variant="body2">Waitlist</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
          {(() => {
            const invites = invitationSummary[invitationDialogLevel]?.invites || [];
            const groups = [
              {
                title: 'Accepted',
                color: 'success.main',
                items: invites.filter((invite) => invite.status === INVITATION_STATUS.ACCEPTED),
              },
              {
                title: 'Awaiting response',
                color: 'info.main',
                items: invites.filter(
                  (invite) =>
                    invite.status === INVITATION_STATUS.SENT || invite.status === INVITATION_STATUS.PROMOTED
                ),
              },
              {
                title: 'Declined / Withdrawn',
                color: 'error.main',
                items: invites.filter(
                  (invite) => invite.status === INVITATION_STATUS.DECLINED || invite.status === INVITATION_STATUS.WITHDRAWN
                ),
              },
            ];
            if (!invites.length) return null;
            return (
              <Box sx={{ mb: 3 }}>
                {groups.map((group) => (
                  <Box key={group.title} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: group.color, mb: 0.5 }}>
                      {group.title} ({group.items.length})
                    </Typography>
                    {group.items.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        None
                      </Typography>
                    ) : (
                      group.items.map((invite) => {
                        const actorSummary = getInvitationActorSummary(invite);
                        const responseNote = getInvitationResponseNote(invite);
                        return (
                          <Box key={`${invite.skaterName}-${invite.rank}`} sx={{ mb: 0.75 }}>
                            <Typography variant="body2">
                              #{invite.rank} {invite.skaterName}
                              {actorSummary ? ` — ${actorSummary}` : ''}
                            </Typography>
                            {responseNote ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1 }}>
                                Note: {responseNote}
                              </Typography>
                            ) : null}
                          </Box>
                        );
                      })
                    )}
                  </Box>
                ))}
              </Box>
            );
          })()}
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {invitationSummary[invitationDialogLevel].pending > 0 && (
              <Chip label={`${invitationSummary[invitationDialogLevel].pending} Pending`} color="default" size="small" />
            )}
            {invitationSummary[invitationDialogLevel].declined > 0 && (
              <Chip label={`${invitationSummary[invitationDialogLevel].declined} Declined`} color="error" size="small" />
            )}
            {invitationSummary[invitationDialogLevel].withdrawn > 0 && (
              <Chip label={`${invitationSummary[invitationDialogLevel].withdrawn} Withdrawn`} color="warning" size="small" />
            )}
            {invitationSummary[invitationDialogLevel].promoted > 0 && (
              <Chip label={`${invitationSummary[invitationDialogLevel].promoted} Promoted`} color="success" size="small" variant="outlined" />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setInvitationDialogOpen(false)}>Close</Button>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setInvitationDialogAction('edit')}
            >
              Edit Message
            </Button>
            {invitationSummary[invitationDialogLevel].pending > 0 && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={() => {
                  setInvitationDialogAction('send');
                }}
              >
                Send {invitationSummary[invitationDialogLevel].pending} Pending
              </Button>
            )}
          </Box>
        </>
      )}
    </Box>
  </Dialog>
      <Menu
        anchorEl={inviteMenu.anchorEl}
        open={Boolean(inviteMenu.anchorEl)}
        onClose={() => setInviteMenu({ anchorEl: null, invitation: null, skaterName: '' })}
        onClick={(event) => event.stopPropagation()}
      >
        <MenuItem disabled>
          <ListItemText primary={inviteMenu.skaterName || 'Invitation'} secondary="Update championship RSVP" />
        </MenuItem>
        <MenuItem
          onClick={() => handleManagerInvitationStatus(INVITATION_STATUS.ACCEPTED)}
          disabled={inviteStatusSaving || inviteMenu.invitation?.status === INVITATION_STATUS.ACCEPTED}
        >
          <ListItemText primary="Mark accepted" />
        </MenuItem>
        <MenuItem
          onClick={() => handleManagerInvitationStatus(INVITATION_STATUS.SENT)}
          disabled={inviteStatusSaving || inviteMenu.invitation?.status === INVITATION_STATUS.SENT}
        >
          <ListItemText primary="Mark awaiting response" />
        </MenuItem>
        <MenuItem
          onClick={() => handleManagerInvitationStatus(INVITATION_STATUS.DECLINED)}
          disabled={inviteStatusSaving || inviteMenu.invitation?.status === INVITATION_STATUS.DECLINED}
        >
          <ListItemText primary="Mark declined" />
        </MenuItem>
        <MenuItem
          onClick={() => handleManagerInvitationStatus(INVITATION_STATUS.WAITLIST)}
          disabled={inviteStatusSaving || inviteMenu.invitation?.status === INVITATION_STATUS.WAITLIST}
        >
          <ListItemText primary="Mark waitlist" />
        </MenuItem>
      </Menu>
      <Dialog
        open={inviteNoteDialog.open}
        onClose={() =>
          !inviteStatusSaving &&
          setInviteNoteDialog({ open: false, status: '', invitation: null, skaterName: '', note: '', error: '' })
        }
        maxWidth="sm"
        fullWidth
        onClick={(event) => event.stopPropagation()}
      >
        <DialogTitle>
          {inviteNoteDialog.status === INVITATION_STATUS.WITHDRAWN ? 'Withdraw invitation' : 'Decline invitation'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Record this as an admin response for {inviteNoteDialog.skaterName}. A note is required so we can tell this
            was not a parent response.
          </Typography>
          {inviteNoteDialog.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {inviteNoteDialog.error}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            required
            multiline
            rows={3}
            label="Note"
            value={inviteNoteDialog.note}
            onChange={(event) =>
              setInviteNoteDialog((prev) => ({ ...prev, note: event.target.value, error: '' }))
            }
            placeholder="e.g. Parent confirmed by phone they cannot attend"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setInviteNoteDialog({ open: false, status: '', invitation: null, skaterName: '', note: '', error: '' })
            }
            disabled={inviteStatusSaving}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={inviteStatusSaving || !inviteNoteDialog.note.trim()}
            onClick={() => handleManagerInvitationStatus(inviteNoteDialog.status, inviteNoteDialog.note)}
          >
            {inviteStatusSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box >
  );
}
